/**
 * Le formulaire d'une fiche — création et modification, le même, pour un
 * client, un transporteur, un site, un véhicule, une remorque, un chauffeur.
 *
 * Écrit à la main, décidé le 03/09/2026 : une quinzaine de champs, sans règle
 * croisée, et une API qui contrôle déjà tout et rend des codes de refus
 * stables. Une bibliothèque de formulaires n'apporterait pas grand-chose ici,
 * et ajouterait une dépendance de plus à lire.
 *
 * Ce composant ne fait que dessiner et rapporter la frappe. Il ne valide rien,
 * n'appelle rien : `validerFiche` et les appels sont ailleurs, en fonctions
 * pures et dans l'écran. C'est ce qui permet de le rendre et de le regarder
 * dans n'importe quel état, fautes comprises.
 *
 * Les champs viennent du descripteur, pas d'ici : ce fichier ne sait pas
 * lesquels il dessine, et c'est ce qui lui permet de servir les deux annuaires.
 */

import {
  choix,
  CONDITIONS_PAIEMENT,
  FREQUENCES_FACTURATION,
  type Champ,
  type Choix,
  type Saisie,
} from './annuaire.js';
import { PAYS } from './pays.js';

/** Les mêmes valeurs, quand ce qui est enregistré est ce qui se lit. */
function memeTexte(valeurs: readonly string[]): readonly Choix[] {
  return valeurs.map((valeur) => ({ valeur, texte: valeur }));
}

/**
 * Les valeurs d'un déroulant fermé.
 *
 * Deux des trois listes sont ÉCRITES DANS LE CODE — pays, conditions de
 * paiement — et ce fichier peut donc aller les chercher. La troisième, les
 * gabarits, vient du référentiel commun chargé à l'exécution : elle arrive par
 * `gabarits`, et vide tant que le référentiel n'est pas là.
 */
function listeDe(
  nom: 'pays' | 'conditions' | 'gabarits' | 'types_vehicule' | 'frequences',
  gabarits: readonly Choix[],
  types: readonly Choix[],
): readonly Choix[] {
  if (nom === 'pays') return memeTexte(PAYS);
  if (nom === 'conditions') return memeTexte(CONDITIONS_PAIEMENT);
  if (nom === 'types_vehicule') return types;
  // LA SEULE LISTE OÙ LA VALEUR N'EST PAS LE TEXTE : `par_envoi` s'enregistre,
  // « Une facture par envoi » se lit. D'où l'absence de `memeTexte`.
  if (nom === 'frequences') return FREQUENCES_FACTURATION;
  return gabarits;
}

interface Proprietes {
  /**
   * Les champs à dessiner. Ce composant ne sait pas de quelle fiche il s'agit
   * — c'est ce qui lui permet de servir les six.
   */
  readonly champs: readonly Champ[];
  /** Le référentiel des gabarits, pour le seul champ qui s'y rapporte. */
  readonly gabarits?: readonly Choix[];
  /** Les types de véhicule de la société, même usage — ajoutés le 04/09/2026. */
  readonly types?: readonly Choix[];
  readonly titre: string;
  readonly saisie: Saisie;
  readonly fautes: Readonly<Record<string, string>>;
  /** Refus que l'API a prononcé et qui ne vise aucun champ en particulier. */
  readonly refus: string | null;
  readonly envoi: boolean;
  readonly quandChamp: (cle: string, valeur: string) => void;
  /**
   * Appelé quand on quitte le champ Nom. C'est là qu'un code est proposé, à la
   * création et seulement si le champ Code est vide : le proposer à chaque
   * lettre frappée le ferait danser sous les yeux.
   */
  readonly quandNomQuitte: () => void;
  readonly quandEnregistrer: () => void;
  readonly quandAnnuler: () => void;
}

export function FormulaireFiche({
  champs,
  gabarits = [],
  types = [],
  titre,
  saisie,
  fautes,
  refus,
  envoi,
  quandChamp,
  quandNomQuitte,
  quandEnregistrer,
  quandAnnuler,
}: Proprietes) {
  return (
    <aside className="panneau">
      <div className="panneau-haut">
        <div className="panneau-titre">
          <div>
            <div className="panneau-nom">{titre}</div>
          </div>
          <button className="fermer" type="button" onClick={quandAnnuler}>
            Fermer ✕
          </button>
        </div>
        {refus !== null && <p className="bandeau">{refus}</p>}
      </div>

      <div className="panneau-corps">
        <form
          className="formulaire"
          onSubmit={(e) => {
            e.preventDefault();
            quandEnregistrer();
          }}
        >
          {champs.map((champ) => {
            const faute = fautes[champ.cle];
            const valeur = saisie[champ.cle] ?? '';
            const identifiant = `champ-${champ.cle}`;
            const classes = ['champ'];
            if (champ.chiffre === true) classes.push('code');
            if (faute !== undefined) classes.push('fautif');

            return (
              <div
                className={champ.court === true ? 'bloc-champ court' : 'bloc-champ'}
                key={champ.cle}
              >
                <label className="etiquette" htmlFor={identifiant}>
                  {champ.etiquette}
                  {champ.unite !== undefined && (
                    <span className="sourdine"> · en {champ.unite}</span>
                  )}
                  {champ.obligatoire === true && (
                    <span className="sourdine"> · obligatoire</span>
                  )}
                </label>

                {champ.liste !== undefined ? (
                  <select
                    className={classes.join(' ')}
                    id={identifiant}
                    value={valeur}
                    disabled={envoi}
                    aria-describedby={
                      faute === undefined ? undefined : `${identifiant}-err`
                    }
                    onChange={(e) => quandChamp(champ.cle, e.target.value)}
                  >
                    {/* Le champ reste facultatif : on peut le laisser vide. */}
                    <option value="">—</option>
                    {choix(listeDe(champ.liste, gabarits, types), valeur).map((c) => (
                      <option value={c.valeur} key={c.valeur}>
                        {c.texte}
                      </option>
                    ))}
                  </select>
                ) : champ.long === true ? (
                  <textarea
                    className={classes.join(' ')}
                    id={identifiant}
                    rows={3}
                    value={valeur}
                    disabled={envoi}
                    aria-describedby={
                      faute === undefined ? undefined : `${identifiant}-err`
                    }
                    onChange={(e) => quandChamp(champ.cle, e.target.value)}
                  />
                ) : (
                  <input
                    className={classes.join(' ')}
                    id={identifiant}
                    value={valeur}
                    disabled={envoi}
                    aria-describedby={
                      faute === undefined ? undefined : `${identifiant}-err`
                    }
                    onChange={(e) => quandChamp(champ.cle, e.target.value)}
                    onBlur={champ.cle === 'nom' ? quandNomQuitte : undefined}
                  />
                )}

                {faute !== undefined && (
                  <span className="erreur-champ" id={`${identifiant}-err`}>
                    {faute}
                  </span>
                )}
              </div>
            );
          })}

          <div className="actions">
            <button className="bouton" type="submit" disabled={envoi}>
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              className="bouton bouton-discret"
              type="button"
              disabled={envoi}
              onClick={quandAnnuler}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </aside>
  );
}
